/**
 * DESURA DE SEHR — Hero Section JS (Fixed)
 * Scroll-driven animation: spring physics + rAF
 */
'use strict';

/* ─── Utilities ─────────────────────────────────────────── */
function mapRange(val, inMin, inMax, outMin, outMax, clamp = true) {
  if (inMin === inMax) return outMin;
  const t = (val - inMin) / (inMax - inMin);
  const r = outMin + t * (outMax - outMin);
  if (!clamp) return r;
  return outMin < outMax
    ? Math.min(outMax, Math.max(outMin, r))
    : Math.min(outMin, Math.max(outMax, r));
}

function multiMap(p, stops) {
  for (let i = 0; i < stops.length - 1; i++) {
    const [x0, y0] = stops[i];
    const [x1, y1] = stops[i + 1];
    if (p <= x1) return mapRange(p, x0, x1, y0, y1);
  }
  return stops[stops.length - 1][1];
}

/* ─── Spring ────────────────────────────────────────────── */
class Spring {
  constructor({ stiffness = 100, damping = 20 } = {}) {
    this.k = stiffness; this.d = damping;
    this.v = 0; this.x = 0; this.target = 0;
  }
  setTarget(t) { this.target = t; }
  step(dt = 1 / 60) {
    const force = -this.k * (this.x - this.target);
    const damp  = -this.d * this.v;
    this.v += ((force + damp)) * dt;
    this.x += this.v * dt;
    return this.x;
  }
}

/* ─── Box config ────────────────────────────────────────── */
const BOX_DATA = [
  { start: 0.03, dur: 0.22, from: -900,  tilt: -3, drift: -6, idx: 0 },
  { start: 0.10, dur: 0.22, from: -1100, tilt:  2, drift:  4, idx: 1 },
  { start: 0.18, dur: 0.22, from: -1000, tilt: -2, drift: -3, idx: 2 },
  { start: 0.26, dur: 0.24, from: -1200, tilt:  4, drift:  7, idx: 3 },
];

/* ─── Idle float ────────────────────────────────────────── */
function getIdleY(idx, ms) {
  const period = 4 + idx * 0.4;
  return Math.sin((ms / 1000 / period) * Math.PI * 2) * -4;
}

/* ─── Sparkle bokeh ─────────────────────────────────────── */
function generateSparkles() {
  const wrap = document.getElementById('sparklesWrap');
  if (!wrap) return;
  const count = window.innerWidth <= 768 ? 6 : 28;
  for (let i = 0; i < count; i++) {
    const x   = (i * 37) % 100;
    const y   = 20 + (i * 53) % 70;
    const sz  = 2 + (i * 7)  % 5;
    const dur = 2 + (i * 11) % 6;
    const dly = (i % 8) * 0.35;
    const el  = document.createElement('div');
    el.className = 'sparkle';
    el.style.cssText =
      `left:${x}%;top:${y}%;width:${sz}px;height:${sz}px;` +
      `--dur:${dur}s;--dly:${dly}s;`;
    wrap.appendChild(el);
  }
}

/* ─── Ribbon progress ───────────────────────────────────── */
function setRibbonProgress(p) {
  const opacity = multiMap(p, [[0.05, 0], [0.2, 1], [1, 0.9]]);
  const draw    = mapRange(p, 0.1, 0.55, 1, 0, true);
  document.querySelectorAll('.ribbon').forEach(r => {
    r.style.strokeDashoffset = draw;
    r.style.opacity = opacity;
  });
}

/* ─── Halo ──────────────────────────────────────────────── */
function updateHalo(p) {
  const wrap = document.getElementById('atmHaloWrap');
  if (!wrap) return;
  const opacity = multiMap(p, [[0, 0], [0.15, 0.55], [0.5, 0.9], [1, 0.75]]);
  const scale   = multiMap(p, [[0, 0.85], [0.6, 1], [1, 1]]);
  const rotate  = mapRange(p, 0, 1, 0, 25);
  wrap.style.opacity   = opacity;
  wrap.style.transform = `translate(-50%,-50%) scale(${scale}) rotate(${rotate}deg)`;
}

/* ─── Silk drift ────────────────────────────────────────── */
function updateSilk(p) {
  const silk = document.getElementById('atmSilk');
  if (!silk) return;
  const x = mapRange(p, 0, 1, -2, 2);
  const y = mapRange(p, 0, 1,  0, -4);
  silk.style.transform = `translate(${x}%,${y}%)`;
}

/* ─── Zero-Reflow Cached Scroll progress ─────────────────── */
let cachedHeroTop = 0;
let cachedScrollable = 1;

function updateHeroGeometry(heroEl) {
  if (!heroEl) return;
  const rect = heroEl.getBoundingClientRect();
  cachedHeroTop = window.scrollY + rect.top;
  cachedScrollable = heroEl.offsetHeight - window.innerHeight;
  if (cachedScrollable <= 0) cachedScrollable = 1;
}

function getScrollProgress() {
  const scrolled = window.scrollY - cachedHeroTop;
  return Math.min(1, Math.max(0, scrolled / cachedScrollable));
}

function parseCoord(val, fallback) {
  if (val !== undefined && val !== null && val !== '') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
  }
  return fallback;
}

/* ─── Main init ─────────────────────────────────────────── */
function initHero() {
  /* Use class selector — works regardless of dynamic section ID */
  const heroEl = document.querySelector('.hero-section');

  /* If no hero section on this page, bail out */
  if (!heroEl) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Grab all dynamic box elements */
  const boxWraps = Array.from(heroEl.querySelectorAll('.box-wrap'));

  /* Extract block configuration dynamically from dataset */
  const blocksData = boxWraps.map((wrap, i) => {
    const ds = wrap.dataset;
    return {
      wrap,
      idx: i,
      start: parseFloat(ds.start) || 0.05,
      dur: parseFloat(ds.dur) || 0.22,
      from: parseFloat(ds.from) || -900,
      landX_en: parseCoord(ds.landXEn || ds.landX, 0),
      landY_en: parseCoord(ds.landYEn || ds.landY, 0),
      landX_ar: parseCoord(ds.landXAr, parseCoord(ds.landXEn || ds.landX, 0) * -1),
      landY_ar: parseCoord(ds.landYAr, parseCoord(ds.landYEn || ds.landY, 0)),
      landX_mb: parseCoord(ds.landXMb, parseCoord(ds.landXEn || ds.landX, 0)),
      landY_mb: parseCoord(ds.landYMb, parseCoord(ds.landYEn || ds.landY, 0)),
      landX_mb_ar: parseCoord(ds.landXMbAr, parseCoord(ds.landXAr, parseCoord(ds.landXEn || ds.landX, 0) * -1)),
      landY_mb_ar: parseCoord(ds.landYMbAr, parseCoord(ds.landYAr, parseCoord(ds.landYEn || ds.landY, 0))),
      landZ: parseFloat(ds.landZ) || 0,
      rotX: parseFloat(ds.rotX) || 0,
      rotY: parseFloat(ds.rotY) || 0,
      rotZ: parseFloat(ds.rotZ || ds.tilt) || 0,
      scaleVal: parseFloat(ds.scale) || 1.0,
      drift: parseFloat(ds.drift || ds.rotZ) || 0
    };
  });

  /* Build springs (Stiffened on mobile for snappy 1-to-1 thumb scroll sync) */
  const isMobileScreen = window.innerWidth <= 768;
  const springs = blocksData.map(() => ({
    y:     new Spring({ stiffness: isMobileScreen ? 140 : 55, damping: isMobileScreen ? 22 : 16 }),
    x:     new Spring({ stiffness: isMobileScreen ? 130 : 50, damping: isMobileScreen ? 22 : 18 }),
    scale: new Spring({ stiffness: isMobileScreen ? 140 : 60, damping: isMobileScreen ? 22 : 18 }),
  }));

  generateSparkles();

  function updateCoordinates() {
    const isArabic = document.documentElement.dir === 'rtl' || 
                     (document.documentElement.lang && document.documentElement.lang.toLowerCase().startsWith('ar')) ||
                     document.body.classList.contains('rtl') || 
                     window.location.pathname.includes('/ar') || 
                     window.location.search.includes('lang=ar');
    const isMobile = window.innerWidth <= 768;

    blocksData.forEach((bd) => {
      const wrap = bd.wrap;
      if (!wrap) return;

      let curLandX = bd.landX_en;
      let curLandY = bd.landY_en;

      if (isMobile && isArabic) {
        curLandX = bd.landX_mb_ar;
        curLandY = bd.landY_mb_ar;
      } else if (isMobile) {
        curLandX = bd.landX_mb;
        curLandY = bd.landY_mb;
      } else if (isArabic) {
        curLandX = bd.landX_ar;
        curLandY = bd.landY_ar;
      }

      wrap.style.left = `calc(50% + ${curLandX}%)`;
      wrap.style.top  = `calc(50% + ${curLandY}%)`;
    });
  }

  updateCoordinates();
  updateHeroGeometry(heroEl);
  window.addEventListener('resize', function () {
    updateCoordinates();
    updateHeroGeometry(heroEl);
  }, { passive: true });
  window.addEventListener('scroll', function () {
    updateHeroGeometry(heroEl);
  }, { passive: true });

  /* Set initial state */
  blocksData.forEach((bd) => {
    const wrap = bd.wrap;
    if (!wrap) return;

    if (reducedMotion) {
      /* Jump straight to landed position */
      wrap.style.opacity   = '1';
      wrap.style.transform = `translate3d(-50%, -50%, ${bd.landZ}px) rotateX(${bd.rotX}deg) rotateY(${bd.rotY}deg) rotateZ(${bd.rotZ}deg)`;
    } else {
      /* Start hidden above */
      wrap.style.opacity   = '0';
      wrap.style.transform =
        `translate3d(-50%, -50%, ${bd.landZ}px) translateY(${bd.from}px) rotateZ(${bd.drift * 8}deg) scale(0.5)`;
    }
  });

  /* ── Animation loop ── */
  let lastTime = performance.now();
  let smoothP  = 0;

  function tick(timestamp) {
    const dt  = Math.min((timestamp - lastTime) / 1000, 0.05);
    lastTime  = timestamp;

    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      /* On mobile, pure GPU CSS keyframes (mobileHeroDrop) handle 120FPS smooth entrance animation */
      requestAnimationFrame(tick);
      return;
    }

    const rawP = getScrollProgress();
    smoothP += (rawP - smoothP) * 0.14;

    /* Atmosphere */
    if (!reducedMotion) {
      updateSilk(smoothP);
      updateHalo(smoothP);
      setRibbonProgress(rawP);
    } else {
      updateHalo(0.5);
      setRibbonProgress(1);
    }

    /* Boxes */
    blocksData.forEach((bd, i) => {
      const p     = smoothP;
      const start = isMobile ? bd.start * 0.35 : bd.start;
      const dur   = isMobile ? bd.dur * 0.55 : bd.dur;
      const end   = start + dur;
      const wrap  = bd.wrap;
      if (!wrap || reducedMotion) return;

      /* Y target */
      const targetY = mapRange(p, start, end, bd.from, 0, true);

      /* X target */
      const targetX = mapRange(p, start, end, bd.drift * 20, 0, true);

      /* Scale: overshoot then settle */
      let targetScale;
      if (p < start) {
        targetScale = 0.5;
      } else if (p < start + dur * 0.6) {
        targetScale = mapRange(p, start, start + dur * 0.6, 0.5, 1.08);
      } else {
        targetScale = mapRange(p, start + dur * 0.6, end, 1.08, 1);
      }

      /* Opacity fade-in */
      const opacity = mapRange(p, start - 0.03, start + 0.04, 0, 1, true);

      /* Rotate: tumble → overshoot → settle */
      const rotateZ = multiMap(p, [
        [start,        bd.drift * 8],
        [end,          bd.rotZ * 1.4],
        [end + 0.02,   bd.rotZ],
        [1,            bd.rotZ],
      ]);

      springs[i].y.setTarget(targetY);
      springs[i].x.setTarget(targetX);
      springs[i].scale.setTarget(targetScale);
      const sy = springs[i].y.step(dt);
      const sx = springs[i].x.step(dt);
      const ss = springs[i].scale.step(dt);

      /* Idle float after landing */
      const idleY = (p >= end + 0.01) ? getIdleY(i, timestamp) : 0;

      wrap.style.opacity = Math.min(1, opacity);

      /* Velocity-based Motion Blur & Dynamic Drop Shadow */
      const prevY = bd.prevY !== undefined ? bd.prevY : sy;
      const velY = Math.abs(sy - prevY);
      bd.prevY = sy;

      let motionBlur = 0;
      if (p >= start && p < end + 0.02) {
        motionBlur = Math.min(isMobile ? 3.5 : 5.5, velY * 0.18);
      }

      const heightRatio = mapRange(sy, bd.from, 0, 0, 1, true);
      const shadowY = mapRange(heightRatio, 0, 1, isMobile ? 24 : 36, 14, true);
      const shadowBlur = mapRange(heightRatio, 0, 1, isMobile ? 18 : 26, 16, true);
      const shadowOpacity = mapRange(heightRatio, 0, 1, 0.08, 0.22, true);

      if (isMobile && p >= 0.99) {
        wrap.style.filter = '';
      } else {
        wrap.style.filter = `drop-shadow(0px ${shadowY.toFixed(1)}px ${shadowBlur.toFixed(1)}px rgba(6, 35, 30, ${shadowOpacity.toFixed(2)})) blur(${motionBlur.toFixed(1)}px)`;
      }

      wrap.style.transform =
        `translate3d(-50%, -50%, ${bd.landZ}px) translateX(${sx.toFixed(1)}px) translateY(${(sy + idleY).toFixed(1)}px) rotateX(${bd.rotX}deg) rotateY(${bd.rotY}deg) rotateZ(${rotateZ.toFixed(1)}deg) scale(${ss.toFixed(2)})`;
    });

    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
}

/* ─── Boot & Theme Editor Events ──────────────────────────── */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHero);
} else {
  initHero();
}

/* Shopify Theme Customizer Live Preview Listeners */
document.addEventListener('shopify:section:load', initHero);
document.addEventListener('shopify:section:select', initHero);
document.addEventListener('shopify:block:select', initHero);
document.addEventListener('shopify:block:deselect', initHero);

/* ─── Smooth Scroll to Featured Collection Section Engine ─────────────────── */
document.addEventListener('click', function (e) {
  const clickedBox = e.target.closest('.box-wrap, .hero-box-product-link');
  if (!clickedBox) return;

  e.preventDefault();
  e.stopPropagation();

  const targetSection = document.getElementById('our-collection') || 
                        document.querySelector('.featured-collection-section') || 
                        document.querySelector('.featured-collection');

  if (targetSection) {
    targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    window.location.href = '#our-collection';
  }
}, true);

